import {
  closestTo,
  formatISO,
  isAfter,
  isWithinInterval,
  max,
  parseISO,
} from 'date-fns'
import { Timescale } from '@opallabs/concepts'
import { startOfWeekByDay } from '../utils/timeFunctions'

export const DAYFETCHBUFFER = 100
export const WEEKFETCHBUFFER = 10
export const WEEKCOUNT = 2000
export const WEEKWIDTH = 112
export const DAYCOUNT = WEEKCOUNT * 7
export const DAYWIDTH = 16

export const isIndexVisible = (
  index,
  firstVisibleWeekIndex,
  lastVisibleWeekIndex
) => index >= firstVisibleWeekIndex && index <= lastVisibleWeekIndex

export const calculateLargestDayCount = (
  data,
  firstVisibleWeekIndex = 0,
  lastVisibleWeekIndex = 1,
  mode
) => {
  let startWeek = firstVisibleWeekIndex
  let endWeek = lastVisibleWeekIndex
  if (mode === Timescale.Day) {
    startWeek /= 7
    endWeek /= 7
  }
  const startDate = startOfWeekByDay(WEEKCOUNT, startWeek, new Date())
  const endDate = startOfWeekByDay(WEEKCOUNT, endWeek, new Date())
  let largestDayCount = 1
  Object.keys(data).forEach((date) => {
    if (!isWithinInterval(new Date(date), { start: startDate, end: endDate }))
      return
    if (data[date] > largestDayCount) {
      largestDayCount = data[date]
    }
  })

  return largestDayCount
}

export const getFetchParams = (
  unitCount,
  firstFetchMarker,
  lastFetchMarker
) => {
  const firstFetchMarkerDate = startOfWeekByDay(
    unitCount,
    firstFetchMarker,
    new Date()
  )
  const startDateToFetch = formatISO(firstFetchMarkerDate, {
    representation: 'date',
  })
  const lastFetchMarkerDate = startOfWeekByDay(
    unitCount,
    lastFetchMarker,
    new Date()
  )
  const endDateToFetch = formatISO(lastFetchMarkerDate, {
    representation: 'date',
  })

  return { startDateToFetch, endDateToFetch }
}

export const getNearestDateWithData = (histogramData, targetDate) => {
  const formattedDate = formatISO(targetDate, { representation: 'date' })
  const targetDateHasData =
    histogramData[formattedDate] !== 0 &&
    histogramData[formattedDate] !== undefined

  if (targetDateHasData) {
    return targetDate
  }

  const histogramDataDatesList = Object.keys(histogramData).sort()

  if (histogramDataDatesList.length === 0) {
    return targetDate
  }

  const closestDateWithMoments = closestTo(
    targetDate,
    histogramDataDatesList.map(parseISO)
  )

  if (histogramDataDatesList.length === 1) {
    return closestDateWithMoments
  }

  const closestDateIndex = histogramDataDatesList.indexOf(
    formatISO(closestDateWithMoments, { representation: 'date' })
  )
  const lastDatesListIndex = histogramDataDatesList.length - 1
  const closestDateIndexIsFirstIndex = closestDateIndex === 0
  const closestDateIndexIsFinalIndex = closestDateIndex === lastDatesListIndex

  const targetDateIsBetweenPopulatedDates =
    (closestDateIndexIsFirstIndex &&
      isAfter(parseISO(formattedDate), closestDateWithMoments)) ||
    (!closestDateIndexIsFinalIndex &&
      isAfter(parseISO(formattedDate), closestDateWithMoments))

  if (targetDateIsBetweenPopulatedDates) {
    const latestDate = max([
      parseISO(histogramDataDatesList[closestDateIndex + 1]),
      parseISO(histogramDataDatesList[closestDateIndex]),
    ])

    return latestDate
  }

  return closestDateWithMoments
}

/**
 * The closest date after `day` (`step` 1) or before it (`step` -1) that
 * carries data, or null when the data holds none that way.
 */
export const getAdjacentDateWithData = (histogramData, day, step) => {
  const dayKey = formatISO(day, { representation: 'date' })
  const datesWithData = Object.keys(histogramData)
    .filter(
      (date) =>
        histogramData[date] > 0 && (step > 0 ? date > dayKey : date < dayKey)
    )
    .sort()
  const date = step > 0 ? datesWithData[0] : datesWithData.at(-1)

  return date === undefined ? null : parseISO(date)
}

export const isScrollingBack = (newOffset, prevOffset) => newOffset < prevOffset
